import React, { useState} from "react";
import { Link } from "react-router-dom";
import { FaBalanceScale, FaFileContract, FaGavel, FaShieldAlt } from "react-icons/fa";
import "bootstrap/dist/css/bootstrap.min.css";
import Header from "../components/Header";
import Footer from "../components/Footer";
import styled, { keyframes } from "styled-components";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideInLeft = keyframes`
  from { opacity: 0; transform: translateX(-100px); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideInRight = keyframes`
  from { opacity: 0; transform: translateX(100px); }
  to { opacity: 1; transform: translateX(0); }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(50px); }
  to { opacity: 1; transform: translateY(0); }
`;

const FAQContainer = styled.div`
  display: flex;
  gap: 40px;
  width: 100%;
  background: white;
  padding: 40px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
`;

const FAQContent = styled.div`
  flex: 1;
  animation: ${slideInRight} 1s ease-in-out;
`;

const FAQItem = styled.div`
  background: #ffffff;
  margin: 15px 0;
  border-bottom: 1px solid #d1d5db;
  overflow: hidden;
  transition: all 0.3s ease-in-out;
  opacity: 0;
  transform: translateY(20px);
  animation: ${fadeUp} 0.5s ease-in-out forwards;
  animation-delay: ${({ delay }) => delay || "0s"};
`;

const Question = styled.div`
  background: #f9fafb;
  padding: 15px;
  font-size: 18px;
  font-weight: bold;
  color: #333;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background 0.3s ease;

  &:hover {
    background: #eef1f6;
  }
`;

const Answer = styled.div`
  padding: 15px;
  font-size: 16px;
  color: #555;
  background: #ffffff;
  border-top: 1px solid #d1d5db;
  animation: ${fadeIn} 0.5s ease-in-out;
`;

const Icon = styled.span`
  font-size: 22px;
  font-weight: bold;
  color: #E60000;
  transition: transform 0.3s ease;
  ${({ isOpen }) => isOpen && "transform: rotate(180deg);"}
`;

const LeftSection = styled.div`
  flex: 1;
  position: relative;
  overflow: hidden;
  max-height: 400px;
  animation: ${slideInLeft} 1s ease-in-out;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease-in-out;
  }

  &:hover img {
    transform: scale(1.05);
  }
`;

const ExperienceBadge = styled.div`
  position: absolute;
  bottom: 20px;
  left: 20px;
  background: linear-gradient(to right, #228B22, #E60000);
  color: white;
  padding: 10px 18px;
  font-size: 18px;
  font-weight: bold;
`;

const ServiceBox = styled.div`
  background: #f9fafb;
  border-radius: 8px;
  padding: 25px;
  margin-bottom: 20px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  animation: ${fadeUp} 0.5s ease-in-out forwards;
  animation-delay: ${({ delay }) => delay || "0s"};
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
  }
`;

const ReasonBox = styled.div`
  background: linear-gradient(to right, rgba(0, 138, 68, 0.05), rgba(220, 53, 69, 0.05));
  border-left: 4px solid #228B22;
  padding: 20px;
  margin-bottom: 15px;
  border-radius: 0 8px 8px 0;
  transition: all 0.3s ease;
  animation: ${fadeIn} 0.5s ease-in-out;
  animation-delay: ${({ delay }) => delay || "0s"};
  
  &:hover {
    border-left: 4px solid #E60000;
    background: linear-gradient(to right, rgba(0, 138, 68, 0.1), rgba(220, 53, 69, 0.1));
  }
`;

const BusinessLaw = () => {
  const [activeIndex, setActiveIndex] = useState(null);

  const toggleFAQ = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const faqData = [
    { 
      question: "Pourquoi faire appel à un cabinet d'expertise comptable pour du droit des affaires ?", 
      answer: "Les cabinets d'expertise comptable offrent souvent une vision globale de l'entreprise, combinant expertise comptable, fiscale et juridique. Cela permet une approche intégrée et une meilleure compréhension des enjeux." 
    },
    { 
      question: "Quelle est la différence entre un avocat et un expert-comptable en droit des affaires ?", 
      answer: "Les avocats sont spécialisés dans la représentation en justice et le conseil juridique pur. Les experts-comptables peuvent offrir des conseils en droit des affaires, mais leur rôle principal reste l'accompagnement comptable et financier. Ils peuvent aussi proposer des partenariats avec des avocats." 
    },
    { 
      question: "Dans quels domaines du droit des affaires intervenez-vous ?", 
      answer: "Nos services couvrent un large éventail : droit des sociétés, droit commercial, droit des contrats, droit social, et accompagnement en cas de litiges." 
    },
    { 
      question: "Qu'est-ce que le contentieux des affaires ?", 
      answer: "Le contentieux des affaires regroupe les litiges entre entreprises ou entre entreprises et particuliers, portant sur des questions commerciales, contractuelles, ou liées à la propriété intellectuelle, etc." 
    },
    { 
      question: "Comment gérez-vous les contentieux ?", 
      answer: "Nous vous accompagnons dans la résolution des litiges, en privilégiant les solutions amiables (médiation, conciliation) et, si nécessaire, en vous assistant devant les tribunaux, en partenariat avec des avocats." 
    },
    { 
      question: "Accompagnez-vous les entreprises en cas de contrôle fiscal ou social ?", 
      answer: "Absolument. Nous vous assistons durant les contrôles, et vous aidons à préparer les réponses aux administrations." 
    },
  ];

  const services = [
    {
      title: "Droit des sociétés",
      description: "Création de sociétés, modification des statuts, gestion des assemblées générales, conseils en gouvernance."
    },
    {
      title: "Droit commercial",
      description: "Rédaction et négociation de contrats commerciaux, gestion des baux commerciaux, recouvrement de créances."
    },
    {
      title: "Droit social",
      description: "Conseils en matière de contrats de travail, gestion des relations individuelles et collectives, accompagnement lors des contrôles sociaux."
    },
    {
      title: "Contentieux des affaires",
      description: "Assistance en cas de litiges commerciaux, de conflits entre associés, de contentieux avec les administrations."
    },
    {
      title: "Droit fiscal",
      description: "Optimisation fiscale, accompagnement lors des contrôles fiscaux, contentieux fiscal."
    }
  ];

  const reasons = [
    {
      title: "Expertise pluridisciplinaire",
      description: "Nous combinons notre expertise en droit des affaires avec notre connaissance des enjeux comptables et fiscaux, pour vous offrir une vision globale et des conseils adaptés."
    },
    {
      title: "Accompagnement personnalisé",
      description: "Nous vous accompagnons à chaque étape de votre développement, de la création de votre entreprise à la résolution de litiges."
    },
    {
      title: "Approche pragmatique",
      description: "Nous privilégions les solutions pragmatiques et efficaces, en tenant compte de vos objectifs et de vos contraintes."
    },
    {
      title: "Confidentialité et réactivité",
      description: "Nous assurons la confidentialité de vos informations et nous répondons rapidement à vos questions et à vos besoins."
    },
    {
      title: "Réseau de partenaires",
      description: "Nous travaillons en étroite collaboration avec des avocats spécialisés, pour vous apporter une expertise complète et adaptée à vos besoins."
    }
  ];

  return (
    <>
      <Header />

      {/* Banner Section */}
      <section className="service-banner d-flex align-items-center">
        <div className="container text-white text-start">
          <h1 className="fw-bold">Droit des Affaires et Contentieux</h1>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/" className="text-white text-decoration-none">
                  Accueil
                </Link>{" "}
                /{" "}
                <Link to="/services" className="text-white text-decoration-none">
                  Services
                </Link>
              </li>
              <li className="breadcrumb-item active text-white" aria-current="page">
                Droit des Affaires et Contentieux
              </li>
            </ol>
          </nav>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="service-content py-5">
        <div className="container">
          <div className="row">
            <div className="col-lg-12 mb-4">
              <h2 className="fw-bold">Sécurisez votre stratégie juridique</h2>
              <p className="text-muted">
                La complexité du droit des affaires et les risques de contentieux peuvent constituer des obstacles majeurs pour votre entreprise. 
                Notre cabinet d'expertise comptable vous propose un accompagnement juridique complet et personnalisé, pour vous permettre de sécuriser 
                vos opérations et de défendre vos intérêts.
              </p>
              <p className="text-muted">
                Nous transformons les défis juridiques en opportunités, en vous offrant une représentation 
                juridique proactive et une gestion efficace des risques.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="why-us py-5 bg-light">
        <div className="container">
          <h3 className="fw-bold text-center mb-4">Pourquoi nous confier vos enjeux juridiques ?</h3>
          <div className="row">
            {reasons.map((reason, index) => (
              <div className="col-md-6" key={index}>
                <ReasonBox delay={`${index * 0.15}s`}>
                  <h5 className="fw-bold">{reason.title}</h5>
                  <p className="mb-0">{reason.description}</p>
                </ReasonBox>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Services Icons Section */}
      <section className="advantages py-5 text-center">
        <div className="container">
          <h3 className="fw-bold">Nos Services Juridiques</h3>
          <div className="row mt-4">
            <div className="col-md-3">
              <FaBalanceScale className="advantage-icon text-primary" size={40} />
              <p className="fw-bold">Conseil Juridique</p>
            </div>
            <div className="col-md-3">
              <FaFileContract className="advantage-icon text-success" size={40} />
              <p className="fw-bold">Rédaction Contractuelle</p>
            </div>
            <div className="col-md-3">
              <FaGavel className="advantage-icon text-danger" size={40} />
              <p className="fw-bold">Résolution de Litiges</p>
            </div>
            <div className="col-md-3">
              <FaShieldAlt className="advantage-icon text-warning" size={40} />
              <p className="fw-bold">Protection Juridique</p>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Services Section */}
      <section className="services-detail py-5">
        <div className="container">
          <h3 className="fw-bold text-center mb-4">Nos services détaillés</h3>
          <div className="row">
            {services.map((service, index) => (
              <div className="col-md-6" key={index}>
                <ServiceBox delay={`${index * 0.15}s`}>
                  <h5 className="fw-bold">{service.title}</h5>
                  <p className="mb-0">{service.description}</p>
                </ServiceBox>
              </div>
            ))}
          </div>
          <div className="text-center mt-4">
            <h4 className="fw-bold">Votre partenaire juridique de confiance</h4>
            <p>
              Nous vous accompagnons dans toutes les étapes de votre développement, pour vous permettre 
              de sécuriser vos opérations et de défendre vos intérêts. Contactez-nous dès aujourd'hui 
              pour une première consultation.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq-section" className="container-fluid py-5 bg-light">
        <div className="container">
          <h3 className="text-center fw-bold mb-5">Questions Fréquentes sur le Droit des Affaires</h3>
          <FAQContainer>
            <LeftSection>
              <img src={process.env.PUBLIC_URL + "/assets/images/image13.png"} alt="Équipe juridique en consultation" />
              <ExperienceBadge>+20 ans d'expertise</ExperienceBadge>
            </LeftSection>
            
            <FAQContent>
              {faqData.map((faq, index) => (
                <FAQItem key={index} delay={`${index * 0.2}s`}>
                  <Question onClick={() => toggleFAQ(index)}>
                    {faq.question}
                    <Icon isOpen={activeIndex === index}>{activeIndex === index ? "−" : "+"}</Icon>
                  </Question>
                  {activeIndex === index && <Answer>{faq.answer}</Answer>}
                </FAQItem>
              ))}
            </FAQContent>
          </FAQContainer>
        </div>
      </section>

      <Footer />

      <style>{`
        .service-banner {
          background: linear-gradient(to right, rgba(0, 138, 68, 0.8), rgba(220, 53, 69, 0.8)), url("/assets/images/service-banner.jpg");
          background-size: cover;
          background-position: center;
          height: 400px;
          display: flex;
          align-items: center;
        }
        .advantage-icon {
          font-size: 40px;
          margin-bottom: 15px;
        }
      `}</style>
    </>
  );
};

export default BusinessLaw;